import { BoxStaffCoOwnerIcon } from "@images/svgIcons/boxStaff/boxStaffCoOwnerIcon"
import { BoxStaffCoachIcon } from "@images/svgIcons/boxStaff/boxStaffCoachIcon"
import { BoxStaffHeadCoachIcon } from "@images/svgIcons/boxStaff/boxStaffHeadCoachIcon"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { UnionIcon } from "@images/svgIcons/unionIcon"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { roleBoxStaffPermissions } from "@modules/boxStaff/utils/boxStaffUtils"
import { word } from "@modules/core/utils/i18n"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { StackedCards } from "@modules/ui/components/stackedCards"
import { Check, X } from "lucide-react"
import { ReactNode } from "react"
import { useTheme } from "styled-components"

type BoxStaffCardsViewProps = {
  selectedRole?: BoxStaffRole
}

export const BoxStaffCardsView = ({ selectedRole }: BoxStaffCardsViewProps) => {
  const { navigateToAddBoxStaffForm } = useBoxStaffSidebar()

  const cards = boxStaffInformation(selectedRole).map((staff) => (
    <BoxStaffCard key={staff.role} {...staff} onClick={() => navigateToAddBoxStaffForm(staff.role)} />
  ))

  return (
    <div>
      <HUText fontStyle="TL">{word("box.member.cardsView.title")}</HUText>
      <StackedCards cards={cards} className="ml-4 mt-5" />
    </div>
  )
}

type BoxStaffCardConstructor = {
  permissions: { text: string; check: boolean }[]
  description: string
  onClick: () => void
  icon: ReactNode
  role: BoxStaffRole
}

const BoxStaffCard = ({ icon, description, permissions, onClick, role }: BoxStaffCardConstructor) => {
  const theme = useTheme()

  return (
    <div className="flex flex-column align-items-center p-5 h-full gap-3">
      <div className="flex flex-column flex-auto align-items-center">
        {icon}
        <HUText fontStyle="TM">{displayRole(role)}</HUText>
        <HUText fontStyle="BM" color={theme.neutralColor700} className="mt-2 text-center">
          {description}
        </HUText>
      </div>
      <div className="flex flex-column align-items-center gap-3">
        <HUText fontStyle="TS" color={theme.neutralColor700}>
          {word("box.member.cardsView.permissions")}
        </HUText>
        {permissions.map((permission, index) => (
          <div key={`${index}-${permission}`} className="flex align-items-center gap-2 w-full">
            {permission.check ? <Check size={24} color={theme.sucess} /> : <X size={24} color={theme.error} />}
            <HUText fontStyle="TS" color={theme.mainWhite}>
              {permission.text}
            </HUText>
          </div>
        ))}
      </div>
      <HUButton
        type="Left_Icon"
        size="S"
        colorType="Primary"
        text={`${word("add.box.member.cardsView.add")} ${displayRole(role).toLowerCase()}`}
        icon={{ icon: PlusIcon, iconSize: 14 }}
        onClick={onClick}
        className="w-full"
      />
      <UnionIcon
        color={theme.mainWhite}
        className="absolute bottom-0"
        style={{ background: `url(${UnionIcon})`, left: "2.5rem", zIndex: -1, opacity: 0.15 }}
      />
    </div>
  )
}

export const boxStaffInformation = (selectedRole?: BoxStaffRole) => {
  const staffRoles = [
    {
      role: BoxStaffRole.COACH,
      permissions: [
        {
          text: word("box.staff.permissionsModal.feature.planning.seeTimetables"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_TIMETABLES),
        },
        {
          text: word("box.staff.permissionsModal.feature.planning.seeSlots"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
        },
        {
          text: word("box.staff.permissionsModal.feature.programming.manageProgrammings"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
        },
        {
          text: word("box.staff.permissionsModal.feature.offers.seeList"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_OFFERS),
        },
        {
          text: word("box.staff.permissionsModal.feature.members.seeListofLeads"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_LEADS),
        },
        {
          text: word("box.staff.permissionsModal.feature.members.seeStaffMember"),
          check: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(BoxStaffPermissions.SEE_STAFF),
        },
      ],
      description: word("box.staff.permissions.description.COACH"),
      icon: <BoxStaffCoachIcon color={""} size={50} />,
    },
    {
      role: BoxStaffRole.HEAD_COACH,
      permissions: [
        { text: word("huPermissions.features.headCoach.feature1"), check: true },
        {
          text: word("box.staff.permissionsModal.feature.planning.addSlot"),
          check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
        },
        {
          text: word("box.staff.permissionsModal.feature.planning.createActivity"),
          check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.CREATE_ACTIVITY),
        },
        {
          text: word("box.staff.permissionsModal.feature.planning.editSlots"),
          check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_SLOTS),
        },
        {
          text: word("box.staff.permissionsModal.feature.planning.editSession"),
          check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
        },
        {
          text: word("box.staff.permissionsModal.feature.members.editMemberFile"),
          check: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(BoxStaffPermissions.EDIT_MEMBER),
        },
      ],
      description: word("box.staff.permissions.description.HEAD_COACH"),
      icon: <BoxStaffHeadCoachIcon color={""} size={50} />,
    },
    {
      role: BoxStaffRole.CO_OWNER,
      permissions: [
        {
          text: word("huPermissions.features.coOwner.feature1"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.MANAGE_SLOTS),
        },
        {
          text: word("huPermissions.features.coOwner.feature2"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.MANAGE_PROGRAMMING),
        },
        {
          text: word("huPermissions.features.coOwner.feature3"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.EDIT_OFFER),
        },
        {
          text: word("huPermissions.features.coOwner.feature5"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.EDIT_MEMBER_OFFER),
        },
        {
          text: word("huPermissions.features.coOwner.feature7"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.SEE_FINANCIAL),
        },
        {
          text: word("huPermissions.features.coOwner.feature4"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.DELETE_GYM),
        },
        {
          text: word("huPermissions.features.coOwner.feature6"),
          check: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(BoxStaffPermissions.DELETE_GYM),
        },
      ],
      description: word("box.staff.permissions.description.CO_OWNER"),
      icon: <BoxStaffCoOwnerIcon color={""} size={50} />,
    },
  ]

  if (!selectedRole) {
    return staffRoles
  }
  const selectedStaffRoles = staffRoles.filter((staffRole) => staffRole.role === selectedRole)
  const otherStaffRoles = staffRoles.filter((staffRole) => staffRole.role !== selectedRole)

  return selectedStaffRoles.concat(otherStaffRoles)
}
